import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Context providers
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { AdminThemeProvider } from "@/contexts/AdminThemeContext";

// Layout
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { LoadingScreen } from "@/components/LoadingScreen";

// Pages
import { Home } from "@/pages/Home";
import { Shop } from "@/pages/Shop";
import { Cart } from "@/pages/Cart";
import { Checkout } from "@/pages/Checkout";
import { CheckoutSuccess } from "@/pages/CheckoutSuccess";
import { Orders } from "@/pages/Orders";
import { OrderDetails } from "@/pages/OrderDetails";
import { ProductDetail } from "@/pages/ProductDetail";
import { Auth } from "@/pages/Auth";
import { Dashboard } from "@/pages/Dashboard";
import { Wishlist } from "@/pages/Wishlist";
import { Addresses } from "@/pages/Addresses";
import { TermsOfUse } from "@/pages/TermsOfUsePage";
import { PrivacyPolicy } from "@/pages/PrivacyPolicyPage";
import { ReturnPolicy } from "@/pages/ReturnPolicy";
import { FAQ } from "@/pages/FAQ";

// Admin Pages
import { AdminLogin } from "@/pages/AdminLogin";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { AdminPanel } from "@/pages/AdminPanel";
import { AdminProducts } from "@/pages/admin/AdminProducts";
import { AdminOrders } from "@/pages/admin/AdminOrders";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminCoupons } from "@/pages/admin/AdminCoupons";
import { AdminCategories } from "@/pages/admin/AdminCategories";
import { AdminNotifications } from "@/pages/admin/AdminNotifications";
import { AdminSettings } from "@/pages/admin/AdminSettings";

// Cart Drawer Wrapper (needs to be inside Router)
const CartDrawerWrapper = () => {
  const { isDrawerOpen, closeDrawer } = useCart();
  return <CartDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />;
};

// Layout wrapper component
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CartDrawerWrapper />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <>
      {isLoading && <LoadingScreen onLoadingComplete={() => setIsLoading(false)} />}
      <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <AdminThemeProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Layout>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:id" element={<OrderDetails />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/addresses" element={<Addresses />} />

                <Route path="/termos" element={<TermsOfUse />} />
                <Route path="/privacidade" element={<PrivacyPolicy />} />
                <Route path="/devolucao" element={<ReturnPolicy />} />
                <Route path="/faq" element={<FAQ />} />

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/panel" element={<AdminPanel />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/coupons" element={<AdminCoupons />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/notifications" element={<AdminNotifications />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Routes>
              </Layout>
            </BrowserRouter>
            </AdminThemeProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
    </>
  );
}

export default App;
